import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Button,
  Message,
  Icon,
  Segment,
} from "semantic-ui-react";
import { AdminUsersV2 } from "../../../../api";
import { useAuth } from "../../../../hooks";

const usersV2Api = new AdminUsersV2();

function AvatarPreview({ src }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) {
    return (
      <Icon
        name="user circle"
        size="big"
        style={{ color: "#bdbdbd", margin: 0 }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  );
}

/**
 * Modal de edicion segura. Solo permite tocar campos no-identidad:
 *   firstname, lastname, country, phone, address, active, avatar.
 *
 * Email, role, _id, password y populados (teacher/student/etc.) NO se
 * pueden modificar desde aqui — el backend tambien los rechaza con 400.
 *
 * Se renderiza como Modal medium con form sencillo. Al guardar, el modal
 * se cierra y se llama a `onUpdated(updatedUser)` para refrescar el listado
 * y el drawer si esta abierto.
 */
export function UsersV2EditModal({ user, open, onClose, onUpdated }) {
  const { accessToken } = useAuth();
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    country: "",
    phone: "",
    address: "",
    active: true,
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && user) {
      setForm({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        country: user.country || "",
        phone: user.phone || "",
        address: user.address || "",
        active: user.active !== false,
      });
      setAvatarFile(null);
      setAvatarPreview(null);
      setError(null);
    }
  }, [open, user]);

  const handleField =
    (field) =>
    (_, { value, checked }) => {
      setForm((prev) => ({
        ...prev,
        [field]: typeof checked === "boolean" ? checked : value,
      }));
    };

  const handleAvatarChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!user?._id) return;
    try {
      setSubmitting(true);
      setError(null);
      const result = await usersV2Api.update(
        accessToken,
        user._id,
        form,
        avatarFile,
      );
      if (onUpdated) onUpdated(result?.user);
      if (onClose) onClose();
    } catch (e) {
      setError(e?.message || "Error guardando los cambios.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="edit" /> Editar usuario
        {user?.email && <span style={{ color: "#666" }}> — {user.email}</span>}
      </Modal.Header>
      <Modal.Content>
        <Message info size="tiny">
          <Icon name="info circle" />
          Solo se pueden editar campos no-identidad. El email, rol y datos de
          los populados (teacher, paciente, terapeuta, etc.) se gestionan desde
          sus paneles especificos.
        </Message>

        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Nombre</label>
              <Input
                value={form.firstname}
                onChange={handleField("firstname")}
                placeholder="Nombre"
              />
            </Form.Field>
            <Form.Field>
              <label>Apellido</label>
              <Input
                value={form.lastname}
                onChange={handleField("lastname")}
                placeholder="Apellido"
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Pais</label>
              <Input
                value={form.country}
                onChange={handleField("country")}
                placeholder="Pais"
              />
            </Form.Field>
            <Form.Field>
              <label>Telefono</label>
              <Input
                value={form.phone}
                onChange={handleField("phone")}
                placeholder="Telefono"
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>Direccion</label>
            <Input
              value={form.address}
              onChange={handleField("address")}
              placeholder="Direccion"
            />
          </Form.Field>

          <Form.Field>
            <Checkbox
              toggle
              label={form.active ? "Usuario activo" : "Usuario inactivo"}
              checked={form.active}
              onChange={handleField("active")}
            />
          </Form.Field>

          <Form.Field>
            <label>Avatar</label>
            <Segment
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: 12,
              }}
            >
              <AvatarPreview src={avatarPreview || user?.avatar} />
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
              />
              {avatarFile && (
                <span style={{ color: "#666", fontSize: 12 }}>
                  Nuevo archivo: {avatarFile.name}
                </span>
              )}
            </Segment>
          </Form.Field>
        </Form>

        {error && (
          <Message negative style={{ marginTop: 12 }}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          primary
          loading={submitting}
          disabled={submitting}
          onClick={handleSubmit}
          icon
          labelPosition="right"
        >
          Guardar
          <Icon name="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
